import React from 'react';
import { motion } from 'framer-motion';
import { TECH_STACK } from '../data/portfolioData';
import LazyImage from '../../../components/ui/LazyImage';

const PortfolioCard = ({ project, index }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: 'easeOut'
      }
    }
  };

  const imageVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.3 }
    }
  };

  const overlayVariants = {
    hover: {
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="group relative bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-border"
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden bg-gray-100">
        <motion.div variants={imageVariants} className="w-full h-full">
          <LazyImage
            src={project.thumbnail}
            alt={project.title}
            className="w-full h-full"
            aspectRatio={1.5}
          />
        </motion.div>
        
        {/* Overlay on Hover */}
        <motion.div
          initial={{ opacity: 0 }}
          variants={overlayVariants}
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6"
        >
          <div className="text-white">
            <p className="text-sm font-medium mb-1">{project.client}</p>
            <p className="text-xs opacity-90">{project.duration} · {project.teamSize}명</p>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-foreground mb-2">{project.title}</h3>
        <p className="text-foreground/70 text-sm mb-4 line-clamp-2">{project.description}</p>

        {/* Tech Stack */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.technologies.slice(0, 4).map((tech) => (
            <span
              key={tech}
              className="px-2 py-1 text-xs font-medium rounded-full"
              style={{
                backgroundColor: `${TECH_STACK[tech].color}20`,
                color: TECH_STACK[tech].color
              }}
            >
              {TECH_STACK[tech].name}
            </span>
          ))}
          {project.technologies.length > 4 && (
            <span className="px-2 py-1 text-xs font-medium text-foreground/50">
              +{project.technologies.length - 4}
            </span>
          )}
        </div>

        {/* Key Results */}
        <div className="pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(project.results).slice(0, 2).map(([key, value]) => (
              <div key={key} className="text-center">
                <p className="text-lg font-bold text-blue-600">{value}</p>
                <p className="text-xs text-foreground/50 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* View Details Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="mt-4 w-full py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
        >
          자세히 보기
        </motion.button>
      </div>
    </motion.div>
  );
};

export default PortfolioCard;